import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { RMReplyTemplate } from '@brightlocal/api-client';

import { apiClient } from 'shared/apiClient';
import type { ErrorReporting } from 'shared/utils';

import type { TemplateFormValues } from './useTemplateFormSchema';
import { REPLY_TEMPLATES_QUERY_KEYS } from '../consts';
import {
  buildCreateTemplateSettings,
  getChangedTemplateSettings,
  isHandledTemplateSaveError,
  isTemplateGoneError
} from '../utils';

/**
 * Creates a template, or saves the changed fields of `template`. The template list refetches after a save, and after
 * an edit finds the template gone.
 */
export const useSaveTemplateMutation = (template: RMReplyTemplate | null) => {
  const queryClient = useQueryClient();
  const isEdit = template !== null;
  const refetchTemplates = () => queryClient.invalidateQueries({ queryKey: [REPLY_TEMPLATES_QUERY_KEYS.TEMPLATES] });

  return useMutation({
    mutationKey: [REPLY_TEMPLATES_QUERY_KEYS.SAVE_TEMPLATE],
    mutationFn: (values: TemplateFormValues) =>
      template
        ? apiClient.rm.updateReplyTemplate(template.id, getChangedTemplateSettings({ template, values }))
        : apiClient.rm.createReplyTemplate(buildCreateTemplateSettings(values)),
    meta: {
      errorReporting: {
        feature: 'review-manager',
        errorMessage: 'Reply template save failed',
        resolve: (error, defaultReport) => (isHandledTemplateSaveError({ error, isEdit }) ? null : defaultReport)
      } satisfies ErrorReporting
    },
    onSuccess: () => {
      void refetchTemplates();
    },
    onError: (error) => {
      if (isEdit && isTemplateGoneError(error)) {
        void refetchTemplates();
      }
    }
  });
};
